import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import Loader from '../../components/Loader';
import formatDate from '../../components/formatDate';
import getISTDate from '../../components/getISTDate';
import MainButton from '../../components/ul/MainButton';
import { toast } from 'react-toastify';
import {
    FaPlus, FaTrash, FaEdit, FaHistory, FaBug, FaTimes, FaSave
} from 'react-icons/fa';
import { FiCalendar, FiRefreshCw, FiFilter } from 'react-icons/fi';

const BugReport = () => {
    const [loader, setLoader] = useState(false);
    const [activeTab, setActiveTab] = useState('history');

    // Active Bug Reports State
    const [reports, setReports] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [messageList, setMessageList] = useState(['']);

    // History State
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLimit] = useState(10);
    const [selectedHistory, setSelectedHistory] = useState([]);

    // Date Filter State (similar to Home)
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [selectedBtn, setSelectedBtn] = useState('today');

    // Refresh State
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        if (activeTab === 'active') {
            fetchReports();
        } else {
            fetchHistory();
        }
    }, [activeTab, historyPage, startDate, endDate]);

    // Date Range Filter Handler (same as Home)
    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istTime = new Date(date.getTime() + istOffset);
            return istTime.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);
                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);
                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;

            case 'thisYear':
                start = toIST(new Date(todayUTC.getFullYear(), 0, 1));
                end = toIST(new Date(todayUTC.getFullYear(), 11, 31));
                setSelectedBtn('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
        setHistoryPage(1);
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectedBtn('range');
        } else {
            setEndDate(e.target.value);
            setSelectedBtn('range');
        }
        setHistoryPage(1);
    };

    // --- Active Reports Functions ---
    const fetchReports = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getReportBug();
            if (resp.status) {
                setReports(resp.data);
                setLastUpdated(new Date());
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch reports");
        } finally {
            setLoader(false);
            setIsRefreshing(false);
        }
    };

    const handleMessageChange = (index, value) => {
        const newList = [...messageList];
        newList[index] = value;
        setMessageList(newList);
    };

    const handleOpenModal = (report = null) => {
        if (report) {
            setEditId(report._id);
            setMessageList(report.report_message || ['']);
        } else {
            setEditId(null);
            setMessageList(['']);
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        const validMessages = messageList.filter(msg => msg.trim() !== '');
        if (validMessages.length === 0) {
            toast.error("Please enter at least one bug report message");
            return;
        }

        setLoader(true);
        try {
            let resp;
            if (editId) {
                resp = await Calling.updateReportBug(editId, { report_message: validMessages });
            } else {
                resp = await Calling.addReportBug({ report_message: validMessages });
            }

            if (resp.status) {
                toast.success(resp.message);
                setIsModalOpen(false);
                fetchReports();
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Operation failed");
        } finally {
            setLoader(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this report?")) return;

        setLoader(true);
        try {
            const resp = await Calling.deleteReportBug(id);
            if (resp.status) {
                toast.success(resp.message);
                fetchReports();
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Delete failed");
        } finally {
            setLoader(false);
        }
    };

    // --- History Functions ---
    const fetchHistory = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getReportBugHistory(historyPage, historyLimit, startDate, endDate);
            if (resp.status) {
                setHistory(resp.data);
                setHistoryTotal(resp.pagination?.total || 0);
                setLastUpdated(new Date());
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch history");
        } finally {
            setLoader(false);
            setIsRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        if (activeTab === 'active') {
            await fetchReports();
        } else {
            await fetchHistory();
        }
    };

    const handleHistorySelect = (id) => {
        if (selectedHistory.includes(id)) {
            setSelectedHistory(selectedHistory.filter(item => item !== id));
        } else {
            setSelectedHistory([...selectedHistory, id]);
        }
    };

    const handleBulkDeleteHistory = async () => {
        if (selectedHistory.length === 0) {
            toast.warn("No items selected");
            return;
        }
        if (!window.confirm(`Delete ${selectedHistory.length} history items?`)) return;

        setLoader(true);
        try {
            const resp = await Calling.bulkDeleteReportBugHistory(selectedHistory);
            if (resp.status) {
                toast.success(resp.message);
                setSelectedHistory([]);
                fetchHistory();
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Bulk delete failed");
        } finally {
            setLoader(false);
        }
    };

    // Stats Cards Component
    const StatsCards = () => {
        const cardConfigs = [
            {
                show: activeTab === 'active',
                data: reports.length,
                label: 'Total Bug Options',
                icon: <FaBug size={22} />,
                borderColor: 'border-main-delete/30',
                iconBgColor: 'bg-main-delete',
            },
            {
                show: activeTab === 'history',
                data: historyTotal,
                label: 'Total Reports',
                icon: <FaHistory size={22} />,
                borderColor: 'border-main-exBlue/30',
                iconBgColor: 'bg-main-exBlue',
            },
        ];

        const visibleCards = cardConfigs.filter(card => card.show);

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {visibleCards.map((card, index) => (
                    <div
                        key={index}
                        className={`
                            group relative p-5 rounded-2xl
                            bg-main-white border ${card.borderColor}
                            shadow-sm hover:shadow-xl 
                            transition-all duration-300 
                            hover:-translate-y-1.5 
                            hover:border-main-blue
                        `}
                    >
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-main-blue to-transparent opacity-0 group-hover:opacity-10 blur-lg rounded-2xl transition duration-300"></div>

                        <div className="relative flex justify-between items-center">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-main-subfont">
                                    {card.label}
                                </p>
                                <h4 className="text-3xl font-bold tracking-tight text-main-font mt-1">
                                    {card.data}
                                </h4>
                            </div>

                            <div
                                className={`
                                    flex items-center justify-center rounded-2xl
                                    p-3 shadow-md
                                    ${card.iconBgColor}
                                    text-main-white
                                    transition-all duration-300
                                    transform group-hover:scale-110
                                `}
                            >
                                {card.icon}
                            </div>
                        </div>

                        <div className="mt-3 h-1 w-0 group-hover:w-full bg-main-blue transition-all duration-500 rounded-full"></div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            {
                loader ? (
                    <Loader />
                ) : (
                    <div className="w-full min-h-screen bg-main-bg text-main-font rounded-2xl">
                        <div className="p-4">
                            {/* Header Section */}
                            <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6 mb-6">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-main-th rounded-xl flex items-center justify-center border border-main-blue/20">
                                            <FaBug className="text-main-blue text-xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-xl md:text-2xl font-bold text-main-font">Bug Reports Management</h1>
                                            <p className="text-main-subfont text-sm mt-0.5">Manage reported bugs and view history</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="bg-main-bg px-4 py-2.5 rounded-xl border border-main-border text-sm text-main-subfont flex items-center gap-2">
                                            <FiCalendar className="text-main-subfont" size={16} />
                                            <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                                        </div>
                                        <button
                                            onClick={handleRefresh}
                                            disabled={isRefreshing}
                                            className={`bg-main-blue hover:bg-[#2db84d] text-main-white px-4 py-2.5 rounded-xl border border-main-blue text-sm font-semibold flex items-center gap-2 transition-all duration-200 shadow-md shadow-main-blue/25 ${isRefreshing ? 'opacity-70 cursor-not-allowed' : ''
                                                }`}
                                            title="Refresh Data"
                                        >
                                            <FiRefreshCw className={`${isRefreshing ? 'animate-spin' : ''}`} size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Tab Navigation & Date Filters - Combined Card */}
                            <div className="bg-main-white rounded-2xl border border-main-border shadow-sm overflow-hidden mb-6">
                                {/* Tab Buttons */}
                                <div className="p-5 md:p-6 border-b border-main-border">
                                    <div className="flex items-center mb-4">
                                        <FiFilter className="text-main-blue mr-2" />
                                        <h2 className="text-lg font-bold text-main-font">View Options</h2>
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <MainButton
                                            onClick={() => setActiveTab('active')}
                                            label={<><FaBug className="inline mr-2" /> Active Reports</>}
                                            selectState={activeTab === 'active'}
                                        />
                                        <MainButton
                                            onClick={() => setActiveTab('history')}
                                            label={<><FaHistory className="inline mr-2" /> User History</>}
                                            selectState={activeTab === 'history'}
                                        />
                                    </div>
                                </div>

                                {/* Date Range Filters - Only for History Tab */}
                                {activeTab === 'history' && (
                                    <div className="p-5 md:p-6 bg-main-bg">
                                        <div className="flex items-center mb-4">
                                            <FiCalendar className="text-main-blue mr-2" />
                                            <h2 className="text-lg font-bold text-main-font">Date Range</h2>
                                        </div>
                                        <div className="grid grid-cols-12 gap-4">
                                            <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                                                <MainButton
                                                    onClick={() => handleDateRangeFilter('today')}
                                                    label={'Today'}
                                                    selectState={selectedBtn === "today"}
                                                />
                                                <MainButton
                                                    onClick={() => handleDateRangeFilter('yesterday')}
                                                    label={'Yesterday'}
                                                    selectState={selectedBtn === "yesterday"}
                                                />
                                                <MainButton
                                                    onClick={() => handleDateRangeFilter('thisWeek')}
                                                    label={'This Week'}
                                                    selectState={selectedBtn === "thisWeek"}
                                                />
                                                <MainButton
                                                    onClick={() => handleDateRangeFilter('thisMonth')}
                                                    label={'This Month'}
                                                    selectState={selectedBtn === "thisMonth"}
                                                />
                                                <MainButton
                                                    onClick={() => handleDateRangeFilter('last6Months')}
                                                    label={'Last 6 Months'}
                                                    selectState={selectedBtn === "last6Months"}
                                                />
                                                <MainButton
                                                    onClick={() => handleDateRangeFilter('thisYear')}
                                                    label={'This Year'}
                                                    selectState={selectedBtn === "thisYear"}
                                                />
                                            </div>

                                            {/* Custom Date Range */}
                                            <div className="col-span-12 md:col-span-4 flex justify-end">
                                                <div className="w-full md:w-auto">
                                                    <div className="grid grid-cols-12 gap-3 justify-items-end">
                                                        <div className="col-span-6 relative">
                                                            <input
                                                                type="date"
                                                                name="startDate"
                                                                value={startDate}
                                                                onChange={handleFormDate}
                                                                min="2023-01-01"
                                                                max={new Date().toISOString().split('T')[0]}
                                                                className={`w-full px-4 py-2.5 rounded-xl border cursor-pointer bg-main-white transition-all ${selectedBtn === "range"
                                                                    ? 'border-main-blue ring-2 ring-main-blue/20 text-main-blue'
                                                                    : 'border-main-border text-main-font hover:border-main-blue/50'
                                                                    }`}
                                                            />
                                                        </div>
                                                        <div className="col-span-6 relative">
                                                            <input
                                                                type="date"
                                                                name="endDate"
                                                                value={endDate}
                                                                onChange={handleFormDate}
                                                                min="2023-01-01"
                                                                max={new Date().toISOString().split('T')[0]}
                                                                className={`w-full px-4 py-2.5 rounded-xl border cursor-pointer bg-main-white transition-all ${selectedBtn === "range"
                                                                    ? 'border-main-blue ring-2 ring-main-blue/20 text-main-blue'
                                                                    : 'border-main-border text-main-font hover:border-main-blue/50'
                                                                    }`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Stats Cards */}
                            <StatsCards />

                            {/* Active Reports Tab */}
                            {activeTab === 'active' && (
                                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm overflow-hidden">
                                    <div className="p-5 md:p-6 border-b border-main-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <div>
                                            <h2 className="text-lg font-bold text-main-font">Bug Report Options</h2>
                                            <p className="text-sm text-main-subfont mt-1">Options shown to users on the front side</p>
                                        </div>
                                        <button
                                            onClick={() => handleOpenModal()}
                                            className="bg-main-blue text-main-white px-5 py-2.5 rounded-xl hover:bg-[#2db84d] transition-all duration-200 flex items-center gap-2 font-semibold shadow-md shadow-main-blue/25 text-sm"
                                        >
                                            <FaPlus /> Add Option
                                        </button>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-main-th border-b border-main-border">
                                                <tr>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider w-16">#</th>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Bug Option</th>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider w-48">Created At</th>
                                                    <th className="px-6 py-4 text-right text-xs font-semibold text-main-font uppercase tracking-wider w-32">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-main-border">
                                                {reports.length > 0 ? (
                                                    reports.map((report, index) => (
                                                        <tr key={report._id} className="hover:bg-main-bg transition-colors duration-150">
                                                            <td className="px-6 py-4">
                                                                <span className="bg-main-th text-main-blue px-3 py-1.5 rounded-lg text-sm font-semibold border border-main-blue/20">
                                                                    {index + 1}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className="text-main-font font-medium">
                                                                    {report.report_message?.[0] || 'No message'}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-main-subfont whitespace-nowrap">
                                                                <div className="flex items-center gap-2">
                                                                    <FiCalendar size={14} className="text-main-subfont" />
                                                                    {formatDate(report.createdAt)}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <div className="flex justify-end gap-2">
                                                                    <button
                                                                        onClick={() => handleOpenModal(report)}
                                                                        className="p-2.5 text-main-exBlue hover:bg-main-exBlue/10 rounded-xl transition-all border border-transparent hover:border-main-exBlue/20"
                                                                        title="Edit"
                                                                    >
                                                                        <FaEdit size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDelete(report._id)}
                                                                        className="p-2.5 text-main-delete hover:bg-main-delete/10 rounded-xl transition-all border border-transparent hover:border-main-delete/20"
                                                                        title="Delete"
                                                                    >
                                                                        <FaTrash size={16} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" className="text-center py-16 text-main-subfont">
                                                            <div className="flex flex-col items-center">
                                                                <div className="w-20 h-20 bg-main-th rounded-full flex items-center justify-center mb-4 border border-main-blue/20">
                                                                    <FaBug className="text-4xl text-main-blue" />
                                                                </div>
                                                                <p className="text-lg font-bold text-main-font">No bug options found</p>
                                                                <p className="text-sm text-main-subfont mt-1">Add your first bug option above</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* History Tab */}
                            {activeTab === 'history' && (
                                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm overflow-hidden">
                                    <div className="p-5 md:p-6 border-b border-main-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <div>
                                            <h2 className="text-lg font-bold text-main-font">User Report History</h2>
                                            <p className="text-sm text-main-subfont mt-1">
                                                {startDate === endDate
                                                    ? `Reports for ${startDate}`
                                                    : `Reports from ${startDate} to ${endDate}`
                                                }
                                            </p>
                                        </div>
                                        {selectedHistory.length > 0 && (
                                            <button
                                                onClick={handleBulkDeleteHistory}
                                                className="bg-main-delete/10 text-main-delete px-5 py-2.5 rounded-xl hover:bg-main-delete/20 transition-all duration-200 flex items-center gap-2 font-semibold border border-main-delete/20 text-sm"
                                            >
                                                <FaTrash /> Delete Selected ({selectedHistory.length})
                                            </button>
                                        )}
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-main-th border-b border-main-border">
                                                <tr>
                                                    <th className="px-4 py-4 w-12">
                                                        <input
                                                            type="checkbox"
                                                            onChange={(e) => {
                                                                if (e.target.checked) setSelectedHistory(history.map(h => h._id));
                                                                else setSelectedHistory([]);
                                                            }}
                                                            checked={history.length > 0 && selectedHistory.length === history.length}
                                                            className="w-4 h-4 rounded text-main-blue focus:ring-main-blue cursor-pointer accent-main-blue"
                                                        />
                                                    </th>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">User</th>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Device Details</th>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Reported Bug</th>
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider w-48">Date & Time</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-main-border">
                                                {history.length > 0 ? (
                                                    history.map((item) => (
                                                        <tr
                                                            key={item._id}
                                                            className={`hover:bg-main-bg transition-colors duration-150 cursor-pointer ${selectedHistory.includes(item._id) ? 'bg-main-th' : ''}`}
                                                            onClick={(e) => {
                                                                // Prevent redirect when clicking checkbox
                                                                if (e.target.type !== 'checkbox') {
                                                                    window.open(`/manage/user/all/info/${item.u_id?._id}`, '_blank');
                                                                }
                                                            }}
                                                        >
                                                            <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedHistory.includes(item._id)}
                                                                    onChange={() => handleHistorySelect(item._id)}
                                                                    className="w-4 h-4 rounded text-main-blue focus:ring-main-blue cursor-pointer accent-main-blue"
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div>
                                                                    <div className="text-sm font-semibold text-main-font">{item.u_id?.u_name || 'Unknown User'}</div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-medium text-main-font">
                                                                        {item.device_brand || ''} {item.device_model || ''}
                                                                        {item.device_name && <span className="text-main-subfont ml-1">({item.device_name})</span>}
                                                                    </span>
                                                                    <span className="text-xs text-main-subfont">
                                                                        {(item.app_version || item.os_version) ? (
                                                                            <>App: v{item.app_version || 'N/A'} | OS: {item.os_version || 'N/A'} {item.sdk_version ? `(SDK ${item.sdk_version})` : ''}</>
                                                                        ) : (
                                                                            item.u_id?.u_device || 'Unknown Device'
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {item.report_message?.map((msg, idx) => (
                                                                        <span
                                                                            key={idx}
                                                                            className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-main-delete/10 text-main-delete border border-main-delete/20"
                                                                        >
                                                                            {msg}
                                                                        </span>
                                                                    )) || <span className="text-main-subfont">No message</span>}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-main-subfont whitespace-nowrap">
                                                                <div className="flex items-center gap-2">
                                                                    <FiCalendar size={14} className="text-main-subfont" />
                                                                    {formatDate(item.createdAt)}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-16 text-main-subfont">
                                                            <div className="flex flex-col items-center">
                                                                <div className="w-20 h-20 bg-main-th rounded-full flex items-center justify-center mb-4 border border-main-blue/20">
                                                                    <FaHistory className="text-4xl text-main-blue" />
                                                                </div>
                                                                <p className="text-lg font-bold text-main-font">No history found</p>
                                                                <p className="text-sm text-main-subfont mt-1">No bug reports in the selected date range</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {historyTotal > historyLimit && (
                                        <div className="px-6 py-4 border-t border-main-border bg-main-th flex flex-col sm:flex-row justify-between items-center gap-4">
                                            <div className="text-sm text-main-subfont">
                                                Showing <span className="font-semibold text-main-font">{((historyPage - 1) * historyLimit) + 1}</span> to <span className="font-semibold text-main-font">{Math.min(historyPage * historyLimit, historyTotal)}</span> of <span className="font-semibold text-main-font">{historyTotal}</span> results
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                                                    disabled={historyPage === 1}
                                                    className="px-4 py-2 border border-main-border rounded-xl hover:bg-main-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-semibold text-main-font bg-main-white"
                                                >
                                                    Previous
                                                </button>
                                                <span className="px-4 py-2 bg-main-blue text-main-white rounded-xl text-sm font-semibold shadow-md shadow-main-blue/25">
                                                    {historyPage}
                                                </span>
                                                <button
                                                    onClick={() => setHistoryPage(p => p + 1)}
                                                    disabled={historyPage * historyLimit >= historyTotal}
                                                    className="px-4 py-2 border border-main-border rounded-xl hover:bg-main-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-semibold text-main-font bg-main-white"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Add/Edit Modal */}
                            {isModalOpen && (
                                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                                    <div className="bg-main-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                                        <div className="px-6 py-5 border-b border-main-border flex justify-between items-center bg-main-th">
                                            <h3 className="text-lg font-bold text-main-font flex items-center gap-2">
                                                <div className="w-9 h-9 bg-main-blue rounded-lg flex items-center justify-center text-main-white">
                                                    {editId ? <FaEdit size={16} /> : <FaPlus size={16} />}
                                                </div>
                                                {editId ? 'Edit Bug Option' : 'Add Bug Option'}
                                            </h3>
                                            <button
                                                onClick={() => setIsModalOpen(false)}
                                                className="w-9 h-9 flex items-center justify-center hover:bg-main-delete/10 rounded-xl transition-all text-main-subfont hover:text-main-delete"
                                            >
                                                <FaTimes size={18} />
                                            </button>
                                        </div>

                                        <div className="p-6">
                                            <label className="block text-sm font-semibold text-main-font mb-2">Bug Description</label>
                                            <input
                                                type="text"
                                                value={messageList[0] || ''}
                                                onChange={(e) => handleMessageChange(0, e.target.value)}
                                                placeholder="Enter bug option (e.g., 'App Crashes')"
                                                className="w-full border border-main-border rounded-xl px-4 py-3 focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue outline-none transition-all duration-200 text-main-font bg-main-white placeholder-main-subfont"
                                                autoFocus
                                            />
                                            <p className="text-xs text-main-subfont mt-2">This will be shown as an option to users when reporting bugs</p>
                                        </div>

                                        <div className="px-6 py-4 border-t border-main-border bg-main-bg flex justify-end gap-3">
                                            <button
                                                onClick={() => setIsModalOpen(false)}
                                                className="px-5 py-2.5 text-main-font hover:bg-main-white rounded-xl transition-all duration-200 font-semibold border border-main-border"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSubmit}
                                                className="px-6 py-2.5 bg-main-blue text-main-white rounded-xl hover:bg-[#2db84d] transition-all duration-200 font-semibold shadow-md shadow-main-blue/25 flex items-center gap-2"
                                            >
                                                <FaSave /> Save
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default BugReport;